import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gavel } from 'lucide-react';
import { useTheme } from './ThemeContext';

const welcomePhrases = [
  { text: "Welcome to  VerdictAI", lang: "English" },
  { text: "कानून क्रांति AI में आपका स्वागत है", lang: "Hindi" },
  { text: "कानून क्रांती AI मध्ये आपले स्वागत आहे", lang: "Marathi" },
  { text: "কানুন ক্রান্তিতে আপনাকে স্বাগতম", lang: "Bengali" },
  { text: "કાનૂન ક્રાંતિમાં આપનું સ્વાગત છે", lang: "Gujarati" },
  { text: "ਕਾਨੂਨ ਕ੍ਰਾਂਤੀ ਵਿੱਚ ਤੁਹਾਡਾ ਸੁਆਗਤ ਹੈ", lang: "Punjabi" },
  { text: "కానూన్ క్రాంతిలో మీకు స్వాగతం", lang: "Telugu" },
  { text: "ಕಾನೂನ್ ಕ್ರಾಂತಿಗೆ ನಿಮ್ಮ ಸ್ವಾಗತ", lang: "Kannada" },
  { text: "കാനൂൺ ക്രാന്തിയിലേക്ക് സ്വാഗതം", lang: "Malayalam" },
  { text: "கனூன் கிராந்திக்கு உங்களை வரவேற்கிறோம்", lang: "Tamil" },
  { text: "कानून क्रांति में आपका स्वागत है", lang: "Nepali" },
  { text: "କାନୁନ୍ କ୍ରାନ୍ତିକୁ ଆପଣଙ୍କୁ ସ୍ଵାଗତ", lang: "Odia" },
  { text: "कानून क्रांति में आपले स्वागत आहे", lang: "Sanskrit" }
];

const subtitles = [
  { text: "Unlock intelligent search of Commercial Court cases with our generative AI.", lang: "English" },
  { text: "हमारे जेनरेटिव AI के साथ वाणिज्यिक न्यायालय मामलों की बुद्धिमान खोज को अनलॉक करें।", lang: "Hindi" },
  { text: "आमच्या जनरेटिव AI सह वाणिज्यिक न्यायालय प्रकरणांचा बुद्धिमान शोध अनलॉक करा.", lang: "Marathi" },
  { text: "আমাদের জেনারেটিভ AI সহ বাণিজ্যিক আদালতের মামলার বুদ্ধিমান অনুসন্ধান আনলক করুন।", lang: "Bengali" },
  { text: "અમારા જનરેટિવ AI સાથે વાણિજ્યિક કોર્ટના કેસોની બુદ્ધિશાળી શોધને અનલૉક કરો.", lang: "Gujarati" },
  { text: "ਸਾਡੇ ਜਨਰੇਟਿਵ AI ਨਾਲ ਵਪਾਰਕ ਅਦਾਲਤ ਦੇ ਮਾਮਲਿਆਂ ਦੀ ਬੁੱਧੀਮਾਨ ਖੋਜ ਨੂੰ ਅਨਲੌਕ ਕਰੋ।", lang: "Punjabi" },
  { text: "మా జెనరేటివ్ AI తో వాణిజ్య కోర్టు కేసుల తెలివైన శోధనను అన్‌లాక్ చేయండి.", lang: "Telugu" },
  { text: "ನಮ್ಮ ಜನರೇಟಿವ್ AI ಯೊಂದಿಗೆ ವಾಣಿಜ್ಯ ನ್ಯಾಯಾಲಯದ ಪ್ರಕರಣಗಳ ಬುದ್ಧಿವಂತ ಹುಡುಕಾಟವನ್ನು ಅನ್‌ಲಾಕ್ ಮಾಡಿ.", lang: "Kannada" },
  { text: "ഞങ്ങളുടെ ജനറേറ്റീവ് AI ഉപയോഗിച്ച് വാണിജ്യ കോടതി കേസുകളുടെ ബുദ്ധിപരമായ തിരയൽ അൺലോക്ക് ചെയ്യുക.", lang: "Malayalam" },
  { text: "எங்கள் உருவாக்க AI மூலம் வணிக நீதிமன்ற வழக்குகளின் அறிவார்ந்த தேடலை திறக்கவும்.", lang: "Tamil" },
  { text: "हाम्रो जेनेरेटिभ AI सँग वाणिज्य अदालतका मुद्दाहरूको बुद्धिमान खोजलाई अनलक गर्नुहोस्।", lang: "Nepali" },
  { text: "ଆମର ଜେନେରେଟିଭ୍ AI ସହିତ ବାଣିଜ୍ୟିକ କୋର୍ଟ ମାମଲାଗୁଡ଼ିକର ବୁଦ୍ଧିମାନ ସନ୍ଧାନକୁ ଅନଲକ୍ କରନ୍ତୁ |", lang: "Odia" },
  { text: "अस्माकं जनरेटिव AI साहाय्येन वाणिज्यिकन्यायालयप्रकरणानां बुद्धिमत्तायुक्तं अन्वेषणं विमुञ्चतु।", lang: "Sanskrit" }
];

const Header: React.FC = () => {
  const { theme } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % welcomePhrases.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const currentPhrase = welcomePhrases[currentIndex];
  const currentSubtitle = subtitles[currentIndex];

  return (
    <div className="w-full flex flex-col items-center justify-center min-h-[180px] py-8 space-y-6">
      <div className="flex flex-col items-center justify-center space-y-4 max-w-2xl mx-auto px-4">
        {/* @ts-expect-error - framer-motion 11.x AnimatePresence types incompatible with TS 4.9.5 */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <div className="flex flex-col items-center space-y-3">
              <h1 className={`text-4xl md:text-5xl font-bold text-center tracking-tight leading-tight ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                {currentPhrase.text}
              </h1>
              <p className={`text-lg md:text-xl text-center font-medium leading-relaxed max-w-xl ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                {currentSubtitle.text}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center space-x-2 mt-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        // Using a span here wrapper since it's inline-flex items helper if needed, 
        // but actually we can just animate the contents if they are wrapped or apply style.
        // Let's just wrap the content in a fragment or simply let the motion div accept style if needed.
        // Wait, the motion.div here was wrapping the whole footer line. 
        // Now I've made the parent a DIV. 
        // So the motion div should probably wrap the inner content effectively?
        // Actually, let's just make the motion.div valid by NOT passing className.
        // I will use style={{ display: 'flex', ... }} if needed or just use the parent div for layout.
        >
          <div className="flex items-center space-x-2">
            <span className={`text-sm uppercase tracking-wider font-semibold ${theme === 'dark' ? 'text-primary-400' : 'text-primary-600'
              }`}>
              Legal Research Engine
            </span>
            <span className={`h-px w-8 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'}`}></span>
            <motion.div
              animate={{ rotate: [0, -10, 10, -5, 5, 0] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatDelay: 5,
                ease: "easeInOut"
              }}
            >
              <Gavel className={`w-5 h-5 ${theme === 'dark' ? 'text-primary-400' : 'text-primary-600'}`} />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Header;